import React, {useCallback, useReducer, useEffect, useContext} from 'react';
import {useSwipeable} from 'react-swipeable';
import get from 'lodash/get';

import SlideshowSlides from '~/src/components/slideshow/slides/SlideshowSlides';
import Layout from '~/src/components/layout/Layout';
import Boomerang from '~/src/components/svg/general/Boomerang';
import KeyboardListener from '~/src/components/listener/keyboard/KeyboardListener';
import RatingButton from '~/src/components/rating/button/RatingButton';
import CommentButton from '~/src/components/comment/button/CommentButton';
import BookmarksButton from '~/src/components/bookmarksButton/BookmarksButton';
import ShareButton from '~/src/components/share/button/ShareButton';

import {SlideTransition} from '~/src/utilities/constants/transitions';
import {ButtonFormat, SlideshowFormat} from '~/src/utilities/constants/formats';
import {isEnabled} from '~/src/utilities/helpers/feature';
import {Feature} from '~/src/utilities/constants/feature';
import {classNames} from '~/src/utilities/helpers/css';
import {fillSlides, preloadSlides, slideshowReducer} from '~/src/utilities/helpers/slideshow';
import {trackGtm} from '~/src/utilities/tracking/gtm';
import {GtmEvents, WemfTypes, InteractionSources} from '~/src/utilities/constants/tracking';
import {sendPixel} from '~/src/utilities/tracking/netmetrix';
import {SettingsContext} from '~/src/components/context/SettingsContext';
import {SiteArea} from '~/src/utilities/constants/navigation';

import css from './Slideshow.module.scss';

const Slideshow = (props) => {
    const {slideshow: slideshowData = {}} = props;
    const {slideshow = {}, slides = [], nextpage} = slideshowData;
    const {meta = {}, isStandalone = false, id: metadataId} = props;

    const {siteArea} = useContext(SettingsContext);

    const [state, dispatch] = useReducer(slideshowReducer, {
        slides: fillSlides(slides, slideshow.slideCount),
        hideControls: false,
        direction: null,
        index: 0,
        isFullscreen: isStandalone,
        nextpage
    });

    const goToSlide = useCallback(
        (steps) => {
            // which way the animation should go
            const direction = steps < 0 ? SlideTransition.SLIDE_LEFT : SlideTransition.SLIDE_RIGHT;

            // keep index in the range [1 - length]
            const index = (slideshow.slideCount + state.index + steps) % slideshow.slideCount;

            dispatch({
                type: 'slide',
                direction,
                index
            });

            trackGtm(GtmEvents.GALLERY_SWIPE, {
                id: metadataId,
                slideshowLength: slideshow.slideCount,
                direction: direction === SlideTransition.SLIDE_LEFT ? 'left' : 'right',
                title: slideshow.title,
                pictureId: get(state.slides, `[${index}].image.id`, ''),
                pictureTitle: get(state.slides, `[${index}].image.captionTitle`, '')
            });

            sendPixel(WemfTypes.SLIDESHOW);
        },
        [state.index]
    );
    const goBack = useCallback(() => goToSlide(-1), [state.index]);
    const goForward = useCallback(() => goToSlide(1), [state.index]);
    const closeFullscreen = isStandalone ? props.onClose : useCallback(() => dispatch({type: 'closeFullscreen'}), [state.isFullscreen]);
    const clickSlide = useCallback(() => dispatch({type: 'clickSlide'}), []);

    const bookmarkCount = Number(get(meta, 'clientdata.community.bookmarks', '0'));
    const likeCount = Number(get(meta, 'clientdata.community.likes', '0'));
    const sharesCount = Number(get(meta, 'clientdata.community.shares', '0'));
    const commentCount = Number(get(meta, 'clientdata.community.comments', '0'));

    const className = classNames(css, 'controller', siteArea, {
        isFullscreen: state.isFullscreen,
        hideControls: state.hideControls
    });

    useEffect(() => {
        if (state.isFullscreen) {
            document.body.style.overflow = 'hidden';

            return () => (document.body.style.overflow = '');
        }
    }, [state.isFullscreen]);

    useEffect(() => {
        if (!isStandalone && state.isFullscreen) {
            trackGtm(GtmEvents.GALLERY_FULLSCREEN, {
                id: metadataId,
                slideshowLength: slideshow.slideCount,
                title: slideshow.title,
                position: props.position
            });

            sendPixel(WemfTypes.SLIDESHOW);
        }
    }, [state.isFullscreen]);

    useEffect(() => {
        preloadSlides(state.slides, state.index, state.nextpage).then((result) => dispatch({...result, type: 'loadSlides'}));
    }, [state.index]);

    const handlers = useSwipeable({
        onSwipedLeft: goForward,
        onSwipedRight: goBack
    });

    return (
        <React.Fragment>
            <KeyboardListener onLeft={goBack} onRight={goForward} onEsc={closeFullscreen} />
            <div className={className} {...handlers}>
                <button className={css.backControl} onClick={goBack}>
                    <Boomerang />
                </button>

                <button className={css.forwardControl} onClick={goForward}>
                    <Boomerang />
                </button>

                {state.isFullscreen && (
                    <React.Fragment>
                        <div className={css.activityBar}>
                            <button className={css.closeControl} onClick={closeFullscreen}></button>
                            <Layout isCentered={true}>
                                <div className={css.activityBarContent}>
                                    <h1 className={css.title}>{slideshow.title}</h1>
                                    <div className={css.activityBarItem}>
                                        {get(meta, 'settings.likesEnabled', true) && (
                                            <RatingButton
                                                metadataId={metadataId}
                                                format={ButtonFormat.BUTTON_ACTION_SLIDESHOW}
                                                activityCount={likeCount}
                                                source={InteractionSources.QUICKACTION_ARTICLE}
                                                onClick={isStandalone ? props.onRatingClick : closeFullscreen}
                                            />
                                        )}
                                        {isEnabled(Feature.COMMENTS) && (
                                            <CommentButton
                                                metadataId={metadataId}
                                                format={ButtonFormat.BUTTON_ACTION_SLIDESHOW}
                                                activityCount={commentCount}
                                                openCommentPage={true}
                                                source={InteractionSources.QUICKACTION_ARTICLE}
                                            />
                                        )}
                                        {isEnabled(Feature.BOOKMARKS) && (
                                            <BookmarksButton
                                                metadataId={metadataId}
                                                format={ButtonFormat.BUTTON_ACTION_SLIDESHOW}
                                                activityCount={bookmarkCount}
                                                source={InteractionSources.QUICKACTION_ARTICLE}
                                            />
                                        )}
                                        {isEnabled(Feature.SHARE) && (
                                            <ShareButton
                                                metadataId={metadataId}
                                                format={ButtonFormat.BUTTON_ACTION_SLIDESHOW}
                                                activityCount={sharesCount}
                                                source={InteractionSources.QUICKACTION_ARTICLE}
                                            />
                                        )}
                                    </div>
                                </div>
                            </Layout>
                        </div>
                    </React.Fragment>
                )}

                {(siteArea !== SiteArea.LIFESTYLE || state.isFullscreen) && (
                    <span className={css.counter}>
                        {state.index + 1} / {slideshow.slideCount}
                    </span>
                )}

                <SlideshowSlides
                    index={state.index}
                    slides={state.slides}
                    direction={state.direction}
                    format={state.isFullscreen ? SlideshowFormat.SLIDESHOW_FULLSCREEN : SlideshowFormat.SLIDESHOW_INLINE}
                    onClick={clickSlide}
                />
            </div>
        </React.Fragment>
    );
};

export default Slideshow;
